<div class="flex flex-col gap-6">
  <div class="ec-category-header">
    {{ 'dashboard.overview.timeseries.title' | translate }}
  </div>

  <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
    <app-status-card [config]="missingTimeSeries"></app-status-card>
    <app-status-card [config]="lockedTimeSeries"></app-status-card>
    <app-status-card [config]="unbalancedQuantity"></app-status-card>
    <app-status-card [config]="timeSeriesNotCreated"></app-status-card>
    <app-status-card [config]="deviatingMeters"></app-status-card>
    <app-status-card [config]="usedVerificationMeterReadings"></app-status-card>
  </div>

  <div class="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">

    <!-- Generated timeseries -->
    <div class="flex h-full min-w-0 flex-col gap-2">
      <span class="ec-category-header">{{ 'dashboard.graph.timeseries.title' | translate }}</span>
      <div class="relative flex min-w-0 flex-col p-5 overflow-hidden bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-sm h-full min-h-[260px]">
        <div class="flex items-start justify-between">
          <span class="text-xs font-normal text-gray-500 dark:text-gray-400 tracking-wide">
            {{ 'dashboard.graph.generated.title' | translate }}
          </span>
          @if (generatedChartLoading) {
            <div class="w-6 h-6 rounded-full bg-gray-100 dark:bg-dark-border animate-pulse"></div>
          }
        </div>
        <div class="relative mt-4 h-[180px] min-h-[180px] flex-none">
          @if (generatedChartLoading) {
            <div class="absolute inset-0 flex items-end gap-0.5 px-1 pb-0">
              @for (w of skeletonBars; track $index) {
                <div class="flex-1 rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse" [style.height.%]="w"></div>
              }
            </div>
          } @else if (generatedChartError) {
            <p class="text-gray-500 dark:text-gray-400 text-sm">{{ 'common.error.fetch' | translate }}</p>
          } @else if (generatedChartEmpty) {
            <div class="absolute inset-0 flex items-center justify-center">
              <p class="text-sm text-gray-300 dark:text-gray-600">{{ 'dashboard.graph.noData' | translate }}</p>
            </div>
          } @else {
            <div class="absolute inset-0">
              <app-echart [options]="generatedChartOptions" height="100%"></app-echart>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Estimated timeseries -->
    <div class="flex h-full min-w-0 flex-col gap-2">
      <span class="ec-category-header">{{ 'dashboard.graph.estimated.title' | translate }}</span>
      <div class="relative flex min-w-0 flex-col p-5 overflow-hidden bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-sm h-full min-h-[260px]">
        <div class="flex items-start justify-between">
          <span class="text-xs font-normal text-gray-500 dark:text-gray-400 tracking-wide">
            {{ 'dashboard.graph.estimated.title' | translate }}
          </span>
          @if (estimatedChartLoading) {
            <div class="w-6 h-6 rounded-full bg-gray-100 dark:bg-dark-border animate-pulse"></div>
          }
        </div>
        <div class="relative mt-4 h-[180px] min-h-[180px] flex-none">
          @if (estimatedChartLoading) {
            <div class="absolute inset-0 flex items-end gap-0.5 px-1 pb-0">
              @for (w of skeletonBars; track $index) {
                <div class="flex-1 rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse" [style.height.%]="w"></div>
              }
            </div>
          } @else if (estimatedChartError) {
            <p class="text-gray-500 dark:text-gray-400 text-sm">{{ 'common.error.fetch' | translate }}</p>
          } @else if (estimatedChartEmpty) {
            <div class="absolute inset-0 flex items-center justify-center">
              <p class="text-sm text-gray-300 dark:text-gray-600">{{ 'dashboard.graph.noData' | translate }}</p>
            </div>
          } @else {
            <div class="absolute inset-0">
              <app-echart [options]="estimatedChartOptions" height="100%"></app-echart>
            </div>
          }
        </div>
        @if (!estimatedChartLoading && !estimatedChartError && !estimatedChartEmpty) {
          <div class="mt-4 flex w-full min-w-0 flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
            @for (item of estimatedLegendItems; track item.method) {
              <span class="inline-flex min-w-0 max-w-full items-start gap-1.5">
                <span class="size-2.5 shrink-0 rounded-sm" [style.background-color]="item.color"></span>
                <span class="min-w-0 whitespace-normal break-words">{{ item.name }}</span>
              </span>
            }
          </div>
        }
      </div>
    </div>

  </div>

  <!-- Grid Loss -->
  <div class="flex flex-col gap-2">
    <span class="ec-category-header">{{ 'gridloss.title' | translate }}</span>

    <div class="flex flex-col w-full bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-sm rounded-sm">

      <!-- Toolbar -->
      <div class="border-b border-b-gray-200 dark:border-dark-border flex justify-between">
        <div class="flex">
          <app-dropdown [directContent]="true">
            <lucide-icon [name]="Calendar" size="16"></lucide-icon>
            {{ 'gridloss.startDate' | translate }}
            <div content>
              <form [formGroup]="gridLossForm">
                <app-input-group>
                  <label appLabel>{{ 'gridloss.startDate' | translate }}</label>
                  <input appInputDate formControlName="startDate" [defaultDate]="false" />
                </app-input-group>
              </form>
            </div>
          </app-dropdown>

          <app-dropdown
            [collection]="gridLossDaysActions"
            [(value)]="selectedDays"
            (valueChange)="onDaysChange($event)"
          >
            {{ 'gridloss.periodLabel' | translate }}
          </app-dropdown>
        </div>

        <div class="flex">
          <button
            class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
            (click)="resetGridLossChart('gridLoss')"
          >
            {{ 'common.reset' | translate }}
          </button>
          <app-dropdown [directContent]="true" [right]="false">
            <lucide-icon [name]="Download" size="15"></lucide-icon>
            {{ 'gridloss.export' | translate }}
            <div content class="flex flex-col -mx-3 -my-2">
              <button
                class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
                (click)="exportImage()"
              >
                <lucide-icon [name]="Download" size="14"></lucide-icon>
                {{ 'gridloss.export.image' | translate }}
              </button>
              <button
                class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
                (click)="exportCsv()"
              >
                <lucide-icon [name]="Download" size="14"></lucide-icon>
                {{ 'gridloss.export.csv' | translate }}
              </button>
            </div>
          </app-dropdown>
          <button
            class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
            (click)="toggleGridLossFullscreen()"
            [title]="'gridloss.fullscreen' | translate"
          >
            <lucide-icon [name]="Maximize2" size="15"></lucide-icon>
          </button>
        </div>
      </div>

      <!-- Chart area -->
      <div class="px-2 pt-4 pb-2 min-h-[400px] relative">
        @if (gridLossLoading) {
          <div class="absolute inset-2 flex items-end gap-0.5 pb-0">
            @for (w of skeletonBars; track $index) {
              <div class="flex-1 rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse" [style.height.%]="w"></div>
            }
          </div>
        } @else if (gridLossError) {
          <p class="text-gray-500 dark:text-gray-400 text-sm">{{ 'common.error.fetch' | translate }}</p>
        } @else if (gridLossEmpty) {
          <div class="absolute inset-0 flex items-center justify-center">
            <p class="text-sm text-gray-300 dark:text-gray-600">{{ 'dashboard.graph.noData' | translate }}</p>
          </div>
        } @else {
          <div class="absolute inset-2">
            <app-echart
              #gridLossChart
              [options]="gridLossChartOptions"
              height="100%"
              (dataZoomChange)="onGridLossDataZoomChange($event, 'gridLoss')"
              (legendSelectionChange)="onGridLossLegendSelectionChange($event, 'gridLoss')"
              (axisPointerChange)="onGridLossAxisPointerChange($event, 'gridLoss')"
            ></app-echart>
          </div>
        }
      </div>

    </div>
  </div>

  <!-- Computed Grid Loss -->
  @if (preferencesService.dashboardComputedGridloss()) {
  <div class="flex flex-col gap-2">
    <span class="ec-category-header">{{ 'gridloss.computed.title' | translate }}</span>

    <!-- Beta disclaimer -->
    <div class="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 text-amber-800 dark:text-amber-300 text-xs">
      <span class="font-semibold shrink-0">{{ 'gridloss.computed.beta.label' | translate }}</span>
      <span>{{ 'gridloss.computed.beta.disclaimer' | translate }}</span>
    </div>

    <div class="flex flex-col w-full bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-sm rounded-sm">

      <!-- Toolbar -->
      <div class="border-b border-b-gray-200 dark:border-dark-border flex justify-between">
        <div class="flex">
          <app-dropdown [directContent]="true">
            <lucide-icon [name]="Calendar" size="16"></lucide-icon>
            {{ 'gridloss.startDate' | translate }}
            <div content>
              <form [formGroup]="gridLossForm">
                <app-input-group>
                  <label appLabel>{{ 'gridloss.startDate' | translate }}</label>
                  <input appInputDate formControlName="startDate" [defaultDate]="false" />
                </app-input-group>
              </form>
            </div>
          </app-dropdown>

          <app-dropdown
            [collection]="gridLossDaysActions"
            [(value)]="selectedDays"
            (valueChange)="onDaysChange($event)"
          >
            {{ 'gridloss.periodLabel' | translate }}
          </app-dropdown>
        </div>

        <div class="flex">
          <label class="flex items-center gap-2 px-3 text-sm text-secondary border-l border-gray-200 dark:border-dark-border">
            <input
              type="checkbox"
              [checked]="synchronizeGridLossCharts"
              (change)="setSynchronizeGridLossCharts($any($event.target).checked)"
            />
            {{ 'gridloss.computed.synchronizeCharts' | translate }}
          </label>
          <button
            class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
            (click)="resetGridLossChart('computed')"
          >
            {{ 'common.reset' | translate }}
          </button>
          <app-dropdown [directContent]="true" [right]="false">
            <lucide-icon [name]="Download" size="15"></lucide-icon>
            {{ 'gridloss.export' | translate }}
            <div content class="flex flex-col -mx-3 -my-2">
              <button
                class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
                (click)="exportComputedImage()"
              >
                <lucide-icon [name]="Download" size="14"></lucide-icon>
                {{ 'gridloss.export.image' | translate }}
              </button>
              <button
                class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
                (click)="exportComputedCsv()"
              >
                <lucide-icon [name]="Download" size="14"></lucide-icon>
                {{ 'gridloss.export.csv' | translate }}
              </button>
            </div>
          </app-dropdown>
          <button
            class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
            (click)="toggleComputedFullscreen()"
            [title]="'gridloss.fullscreen' | translate"
          >
            <lucide-icon [name]="Maximize2" size="15"></lucide-icon>
          </button>
        </div>
      </div>

      <!-- Chart area -->
      <div class="px-2 pt-4 pb-2 min-h-[400px] relative">
        @if (computedLoading) {
          <div class="absolute inset-2 flex items-end gap-0.5 pb-0">
            @for (w of skeletonBars; track $index) {
              <div class="flex-1 rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse" [style.height.%]="w"></div>
            }
          </div>
        } @else if (computedError) {
          <p class="text-gray-500 dark:text-gray-400 text-sm">{{ 'common.error.fetch' | translate }}</p>
        } @else if (computedEmpty) {
          <div class="absolute inset-0 flex items-center justify-center">
            <p class="text-sm text-gray-300 dark:text-gray-600">{{ 'dashboard.graph.noData' | translate }}</p>
          </div>
        } @else {
          <div class="absolute inset-2">
            <app-echart
              #computedChart
              [options]="computedChartOptions"
              height="100%"
              (dataZoomChange)="onGridLossDataZoomChange($event, 'computed')"
              (legendSelectionChange)="onGridLossLegendSelectionChange($event, 'computed')"
              (axisPointerChange)="onGridLossAxisPointerChange($event, 'computed')"
            ></app-echart>
          </div>
        }
      </div>

    </div>
  </div>
  }

</div>

@if (preferencesService.dashboardComputedGridloss() && computedFullscreen) {
  <div class="fixed inset-0 z-50 flex flex-col bg-white dark:bg-dark-bg">

    <!-- Fullscreen toolbar -->
    <div class="flex items-center justify-between border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shrink-0">
      <div class="flex">
        <app-dropdown [directContent]="true">
          <lucide-icon [name]="Calendar" size="16"></lucide-icon>
          {{ 'gridloss.startDate' | translate }}
          <div content>
            <form [formGroup]="gridLossForm">
              <app-input-group>
                <label appLabel>{{ 'gridloss.startDate' | translate }}</label>
                <input appInputDate formControlName="startDate" [defaultDate]="false" />
              </app-input-group>
            </form>
          </div>
        </app-dropdown>
        <app-dropdown
          [collection]="gridLossDaysActions"
          [(value)]="selectedDays"
          (valueChange)="onDaysChange($event)"
        >
          {{ 'gridloss.periodLabel' | translate }}
        </app-dropdown>
      </div>

      <div class="flex">
        <button
          class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
          (click)="resetGridLossChart('computed')"
        >
          {{ 'common.reset' | translate }}
        </button>
        <app-dropdown [directContent]="true" [right]="false">
          <lucide-icon [name]="Download" size="15"></lucide-icon>
          {{ 'gridloss.export' | translate }}
          <div content class="flex flex-col -mx-3 -my-2">
            <button
              class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
              (click)="exportComputedImage()"
            >
              <lucide-icon [name]="Download" size="14"></lucide-icon>
              {{ 'gridloss.export.image' | translate }}
            </button>
            <button
              class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
              (click)="exportComputedCsv()"
            >
              <lucide-icon [name]="Download" size="14"></lucide-icon>
              {{ 'gridloss.export.csv' | translate }}
            </button>
          </div>
        </app-dropdown>
        <button
          class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
          (click)="toggleComputedFullscreen()"
          [title]="'gridloss.fullscreen.close' | translate"
        >
          <lucide-icon [name]="X" size="15"></lucide-icon>
        </button>
      </div>
    </div>

    <!-- Fullscreen chart -->
    <div class="relative flex-1 p-2">
      @if (computedLoading) {
        <div class="absolute inset-2 flex items-end gap-0.5 pb-0">
          @for (w of skeletonBars; track $index) {
            <div class="flex-1 rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse" [style.height.%]="w"></div>
          }
        </div>
      } @else if (computedError) {
        <p class="text-gray-500 dark:text-gray-400 text-sm">{{ 'common.error.fetch' | translate }}</p>
      } @else if (computedEmpty) {
        <div class="absolute inset-0 flex items-center justify-center">
          <p class="text-sm text-gray-300 dark:text-gray-600">{{ 'dashboard.graph.noData' | translate }}</p>
        </div>
      } @else {
        <div class="absolute inset-2">
          <app-echart
            #computedChartFullscreen
            [options]="computedChartOptions"
            height="100%"
            (dataZoomChange)="onGridLossDataZoomChange($event, 'computed')"
            (legendSelectionChange)="onGridLossLegendSelectionChange($event, 'computed')"
            (axisPointerChange)="onGridLossAxisPointerChange($event, 'computed')"
          ></app-echart>
        </div>
      }
    </div>

  </div>
}

@if (gridLossFullscreen) {
  <div class="fixed inset-0 z-50 flex flex-col bg-white dark:bg-dark-bg">

    <!-- Fullscreen toolbar -->
    <div class="flex items-center justify-between border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shrink-0">
      <div class="flex">
        <app-dropdown [directContent]="true">
          <lucide-icon [name]="Calendar" size="16"></lucide-icon>
          {{ 'gridloss.startDate' | translate }}
          <div content>
            <form [formGroup]="gridLossForm">
              <app-input-group>
                <label appLabel>{{ 'gridloss.startDate' | translate }}</label>
                <input appInputDate formControlName="startDate" [defaultDate]="false" />
              </app-input-group>
            </form>
          </div>
        </app-dropdown>
        <app-dropdown
          [collection]="gridLossDaysActions"
          [(value)]="selectedDays"
          (valueChange)="onDaysChange($event)"
        >
          {{ 'gridloss.periodLabel' | translate }}
        </app-dropdown>
      </div>

      <div class="flex">
        <button
          class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
          (click)="resetGridLossChart('gridLoss')"
        >
          {{ 'common.reset' | translate }}
        </button>
        <app-dropdown [directContent]="true" [right]="false">
          <lucide-icon [name]="Download" size="15"></lucide-icon>
          {{ 'gridloss.export' | translate }}
          <div content class="flex flex-col -mx-3 -my-2">
            <button
              class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
              (click)="exportImage()"
            >
              <lucide-icon [name]="Download" size="14"></lucide-icon>
              {{ 'gridloss.export.image' | translate }}
            </button>
            <button
              class="flex gap-2 items-center px-3 py-2 text-sm text-secondary hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary whitespace-nowrap"
              (click)="exportCsv()"
            >
              <lucide-icon [name]="Download" size="14"></lucide-icon>
              {{ 'gridloss.export.csv' | translate }}
            </button>
          </div>
        </app-dropdown>
        <button
          class="flex gap-2 items-center justify-center text-sm text-secondary px-3 py-3 border-l border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-primary"
          (click)="toggleGridLossFullscreen()"
          [title]="'gridloss.fullscreen.close' | translate"
        >
          <lucide-icon [name]="X" size="15"></lucide-icon>
        </button>
      </div>
    </div>

    <!-- Fullscreen chart -->
    <div class="relative flex-1 p-2">
      @if (gridLossLoading) {
        <div class="absolute inset-2 flex items-end gap-0.5 pb-0">
          @for (w of skeletonBars; track $index) {
            <div class="flex-1 rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse" [style.height.%]="w"></div>
          }
        </div>
      } @else if (gridLossError) {
        <p class="text-gray-500 dark:text-gray-400 text-sm">{{ 'common.error.fetch' | translate }}</p>
      } @else if (gridLossEmpty) {
        <div class="absolute inset-0 flex items-center justify-center">
          <p class="text-sm text-gray-300 dark:text-gray-600">{{ 'dashboard.graph.noData' | translate }}</p>
        </div>
      } @else {
        <div class="absolute inset-2">
          <app-echart
            #gridLossChartFullscreen
            [options]="gridLossChartOptions"
            height="100%"
            (dataZoomChange)="onGridLossDataZoomChange($event, 'gridLoss')"
            (legendSelectionChange)="onGridLossLegendSelectionChange($event, 'gridLoss')"
            (axisPointerChange)="onGridLossAxisPointerChange($event, 'gridLoss')"
          ></app-echart>
        </div>
      }
    </div>

  </div>
}
